/**
 * Each page's title and actions, above it, and "Add page" below the last. §6.2.
 *
 * Canva's arrangement: "Page 1 - Front" on the left, the page's own actions on
 * the right, set just above the sheet so it is obvious which page they act on.
 * HTML rather than canvas, because these are buttons and a text field - they
 * want focus, keyboard and a screen reader, none of which a canvas has.
 */

import { useState } from 'react';
import { useEditor } from '../state/store';
import { mmToPx } from '../model/units';
import { addPage, deletePage, duplicatePage, movePage, renamePage } from '../state/actions';
import { PAGE_HEAD, fromActive } from './stack';
import { pageLabel } from '../model/types';
import { pagesFixed } from '../api';

/** Space between the last page's bleed and the Add page button. */
const ADD_GAP = 24;

export function PageHeads() {
  const doc = useEditor( ( s ) => s.doc );
  const pageIndex = useEditor( ( s ) => s.pageIndex );
  const view = useEditor( ( s ) => s.view );

  if ( ! doc ) return null;

  const bleed = mmToPx( doc.bleed, view.zoom );
  const last = doc.pages.length - 1;

  /*
   * A product that names its pages - a business card's Front and Back - holds
   * the design to exactly those. The titles still say which page is which and
   * still make a page the active one; nothing adds, removes, moves or renames.
   */
  const fixed = pagesFixed();

  return (
    <>
      { doc.pages.map( ( page, index ) => {
        const at = fromActive( doc, index, pageIndex, view.zoom );
        const left = view.x + at.x - bleed;
        const top = view.y + at.y - bleed;
        const width = mmToPx( page.size.w, view.zoom ) + bleed * 2;
        const height = mmToPx( page.size.h, view.zoom ) + bleed * 2;

        return (
          <div key={ page.id }>
            <div
              className={ `tb-pagehead${ index === pageIndex ? ' is-on' : '' }` }
              style={ { left, top: top - PAGE_HEAD + 6, width } }
            >
              {/*
                A booklet's pages are named by where they fall - the cover,
                pages 8 and 9 - which is what a customer calls them and what
                the printer will ask about.
              */}
              <Title
                index={ index }
                name={ pageLabel( doc, index ) }
                /*
                 * A booklet's titles already say which pages they are - "Front
                 * cover", "Pages 8-9" - so numbering the sheet on top of that
                 * reads as two different page numbers for the same thing.
                 */
                numbered={ ! doc.booklet }
                active={ index === pageIndex }
                fixed={ fixed || !! doc.booklet }
              />

              { ! fixed && (
              <div className="tb-pagehead__actions" role="toolbar" aria-label={ `${ page.name } actions` }>
                <Action label="Move page up" disabled={ index === 0 } onClick={ () => movePage( index, index - 1 ) }>
                  <path d="M5 12l5-5 5 5" />
                </Action>
                <Action label="Move page down" disabled={ index === last } onClick={ () => movePage( index, index + 1 ) }>
                  <path d="M5 8l5 5 5-5" />
                </Action>
                <Action label="Duplicate page" onClick={ () => duplicatePage( index ) }>
                  <rect x="6" y="6" width="10" height="10" rx="1.5" />
                  <path d="M4 13V5a1 1 0 0 1 1-1h8" />
                </Action>
                <Action label="Delete page" disabled={ doc.pages.length < 2 } onClick={ () => deletePage( index ) }>
                  <path d="M4 6h12M8 6V4h4v2M6 6l1 10h6l1-10" />
                </Action>
                <Action label="Add a page" onClick={ () => addPage() }>
                  <path d="M10 5v10M5 10h10" />
                </Action>
              </div>
              ) }
            </div>

            {/* Compact, centred under the last page, with room above it. */}
            { index === last && ! fixed && (
              <button
                type="button"
                className="tb-addpage"
                style={ { left: left + width / 2, top: top + height + ADD_GAP } }
                onClick={ () => addPage() }
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M10 4.5v11M4.5 10h11" />
                </svg>
                Add page
              </button>
            ) }
          </div>
        );
      } ) }
    </>
  );
}

/** "Page 1 - Front", the name editable in place - unless the product names its pages. */
function Title( { index, name, active, fixed, numbered = true }: { index: number; name: string; active: boolean; fixed: boolean; numbered?: boolean } ) {
  const [ editing, setEditing ] = useState( false );
  const [ draft, setDraft ] = useState( name );

  if ( editing ) {
    return (
      <input
        className="tb-pagehead__input"
        value={ draft }
        autoFocus
        maxLength={ 60 }
        aria-label="Page title"
        onChange={ ( event ) => setDraft( event.target.value ) }
        onBlur={ () => {
          renamePage( index, draft );
          setEditing( false );
        } }
        onKeyDown={ ( event ) => {
          if ( event.key === 'Enter' ) event.currentTarget.blur();

          if ( event.key === 'Escape' ) {
            setDraft( name );
            setEditing( false );
          }
        } }
      />
    );
  }

  return (
    <button
      type="button"
      className="tb-pagehead__title"
      title={ fixed ? name : 'Rename this page' }
      onClick={ () => {
        // The first click makes the page the active one; the next renames it,
        // where renaming is the customer's to do.
        if ( ! active ) {
          useEditor.getState().goToPage( index );

          return;
        }

        if ( fixed ) return;

        setDraft( name );
        setEditing( true );
      } }
    >
      { numbered ? (
        <>
          <strong>Page { index + 1 }</strong>
          <span> – { name }</span>
        </>
      ) : (
        <strong>{ name }</strong>
      ) }
    </button>
  );
}

function Action( {
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
} ) {
  return (
    <button type="button" className="tb-pagehead__btn" aria-label={ label } title={ label } disabled={ disabled } onClick={ onClick }>
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        { children }
      </svg>
    </button>
  );
}
